import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./createEditBook.module.css";

const initialBookState = {
  bookImage: null,
  title: "",
  subtitle: "",
  authors: [""],
  aboutAuthors: [{ name: "", about: "" }],
  signature: "",
  isbn: "",
  releaseDate: "",
  hardcoverInfo: "",
  series: "",
  summary: "",
  recommendation: "",
  coverage: [""],
  translatorsNote: "",
  translators: [{ name: "", about: "" }],
  tableOfContents: [{ order: "", item: "" }],
};

export default function CreateEditBook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [bookData, setBookData] = useState(initialBookState);

  useEffect(() => {
    if (isEditing) {
      const fetchBookById = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/v1/books/${id}`
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch book data for ID: ${id}`);
          }
          const data = await response.json();

          const formattedData = {
            ...initialBookState,
            ...data,
            releaseDate: data.releaseDate
              ? new Date(data.releaseDate).toISOString().split("T")[0]
              : "",
            bookImage: null,

            authors: data.authors || [],
            aboutAuthors: data.aboutAuthors || [],
            coverage: data.coverage || [],
            translators: data.translators || [],
            tableOfContents: data.tableOfContents || [],
          };
          setBookData(formattedData);
        } catch (error) {
          console.error("Error fetching book:", error);
          alert("Could not find the book to edit. Redirecting to list.");
          navigate("/admin/book-list");
        }
      };
      fetchBookById();
    } else {
      setBookData(initialBookState);
    }
  }, [id, isEditing, navigate]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setBookData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleListChange = (listName, index, value) => {
    setBookData((prev) => {
      const newList = [...prev[listName]];
      newList[index] = value;
      return { ...prev, [listName]: newList };
    });
  };

  const handleListObjectChange = (listName, index, field, value) => {
    setBookData((prev) => {
      const newList = [...prev[listName]];
      newList[index] = { ...newList[index], [field]: value };
      return { ...prev, [listName]: newList };
    });
  };

  const handleAddItem = (listName, newItem) => {
    setBookData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], newItem],
    }));
  };

  const handleRemoveItem = (listName, index) => {
    setBookData((prev) => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const formData = new FormData();
    Object.keys(bookData).forEach((key) => {
      if (key === "bookImage" && bookData[key]) {
        formData.append(key, bookData[key]);
      } else if (Array.isArray(bookData[key])) {
        formData.append(key, JSON.stringify(bookData[key]));
      } else {
        formData.append(key, bookData[key] || "");
      }
    });

    const url = isEditing
      ? `${process.env.REACT_APP_API_URL}/api/v1/books/${id}`
      : `${process.env.REACT_APP_API_URL}/api/v1/books`;
    const method = isEditing ? "PUT" : "POST";

    console.log(`Submitting with method: ${method} to URL: ${url}`);
    console.log("Submitting Book Data:", bookData);

    alert("Book data has been logged to the console.");
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>
        {isEditing ? `Edit Book (ID: ${id})` : "Add a New Book"}
      </h1>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="bookImage" className={styles.label}>
            Book Image
          </label>
          <input
            id="bookImage"
            name="bookImage"
            type="file"
            onChange={handleChange}
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="title" className={styles.label}>
            Title
          </label>
          <input
            id="title"
            name="title"
            type="text"
            value={bookData.title}
            onChange={handleChange}
            placeholder="Input for title"
            className={styles.input}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="subtitle" className={styles.label}>
            Subtitle (Optional)
          </label>
          <input
            id="subtitle"
            name="subtitle"
            type="text"
            value={bookData.subtitle}
            onChange={handleChange}
            placeholder="Input for subtitle"
            className={styles.input}
          />
        </div>

        <div className={`${styles.formGroup} ${styles.dynamicSection}`}>
          <label className={styles.label}>Authors</label>
          {bookData.authors.map((author, index) => (
            <div key={index} className={styles.dynamicItem}>
              <input
                type="text"
                value={author}
                onChange={(e) =>
                  handleListChange("authors", index, e.target.value)
                }
                placeholder="Input for author name"
                className={styles.input}
              />
              {bookData.authors.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem("authors", index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddItem("authors", "")}
            className={styles.addButton}
          >
            Add More
          </button>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="signature" className={styles.label}>
            Signature
          </label>
          <input
            id="signature"
            name="signature"
            type="text"
            value={bookData.signature}
            onChange={handleChange}
            placeholder="Input for Signature"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="isbn" className={styles.label}>
            ISBN
          </label>
          <input
            id="isbn"
            name="isbn"
            type="text"
            value={bookData.isbn}
            onChange={handleChange}
            placeholder="Input for ISBN"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="releaseDate" className={styles.label}>
            Release Date
          </label>
          <input
            id="releaseDate"
            name="releaseDate"
            type="date"
            value={bookData.releaseDate}
            onChange={handleChange}
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="hardcoverInfo" className={styles.label}>
            Hardcover Info
          </label>
          <input
            id="hardcoverInfo"
            name="hardcoverInfo"
            type="text"
            value={bookData.hardcoverInfo}
            onChange={handleChange}
            placeholder="e.g., 350 pages"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="series" className={styles.label}>
            Series
          </label>
          <input
            id="series"
            name="series"
            type="text"
            value={bookData.series}
            onChange={handleChange}
            placeholder="Input for Series"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="summary" className={styles.label}>
            Summary
          </label>
          <textarea
            id="summary"
            name="summary"
            value={bookData.summary}
            onChange={handleChange}
            placeholder="Text area for summary"
            className={styles.textarea}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="recommendation" className={styles.label}>
            Recommendation
          </label>
          <textarea
            id="recommendation"
            name="recommendation"
            value={bookData.recommendation}
            onChange={handleChange}
            placeholder="Text area for recommendation"
            className={styles.textarea}
          />
        </div>

        <div className={`${styles.formGroup} ${styles.dynamicSection}`}>
          <label className={styles.label}>Coverage</label>
          {bookData.coverage.map((item, index) => (
            <div key={index} className={styles.dynamicItem}>
              <input
                type="text"
                value={item}
                onChange={(e) =>
                  handleListChange("coverage", index, e.target.value)
                }
                placeholder="Input for coverage"
                className={styles.input}
              />
              {bookData.coverage.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem("coverage", index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddItem("coverage", "")}
            className={styles.addButton}
          >
            Add More
          </button>
        </div>

        <div className={`${styles.formGroup} ${styles.dynamicSection}`}>
          <label className={styles.label}>About The Authors</label>
          {bookData.aboutAuthors.map((author, index) => (
            <div key={index} className={styles.dynamicItem}>
              <input
                type="text"
                value={author.name}
                onChange={(e) =>
                  handleListObjectChange(
                    "aboutAuthors",
                    index,
                    "name",
                    e.target.value
                  )
                }
                placeholder={`About author ${index + 1} name`}
                className={styles.input}
              />
              <textarea
                value={author.about}
                onChange={(e) =>
                  handleListObjectChange(
                    "aboutAuthors",
                    index,
                    "about",
                    e.target.value
                  )
                }
                placeholder={`Text area for About author ${index + 1} about`}
                className={styles.textarea}
              />
              {bookData.aboutAuthors.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem("aboutAuthors", index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              handleAddItem("aboutAuthors", { name: "", about: "" })
            }
            className={styles.addButton}
          >
            Add More
          </button>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="translatorsNote" className={styles.label}>
            Translator's Note
          </label>
          <textarea
            id="translatorsNote"
            name="translatorsNote"
            value={bookData.translatorsNote}
            onChange={handleChange}
            placeholder="Text area for translator's note"
            className={styles.textarea}
          />
        </div>

        <div className={`${styles.formGroup} ${styles.dynamicSection}`}>
          <label className={styles.label}>About The Translators</label>
          {bookData.translators.map((translator, index) => (
            <div key={index} className={styles.dynamicItem}>
              <input
                type="text"
                value={translator.name}
                onChange={(e) =>
                  handleListObjectChange(
                    "translators",
                    index,
                    "name",
                    e.target.value
                  )
                }
                placeholder={`About translator ${index + 1} name`}
                className={styles.input}
              />
              <textarea
                value={translator.about}
                onChange={(e) =>
                  handleListObjectChange(
                    "translators",
                    index,
                    "about",
                    e.target.value
                  )
                }
                placeholder={`Text area for About translator ${
                  index + 1
                } about`}
                className={styles.textarea}
              />
              {bookData.translators.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem("translators", index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              handleAddItem("translators", { name: "", about: "" })
            }
            className={styles.addButton}
          >
            Add More
          </button>
        </div>

        <div className={`${styles.formGroup} ${styles.dynamicSection}`}>
          <label className={styles.label}>Table of Contents</label>
          {bookData.tableOfContents.map((toc, index) => (
            <div
              key={index}
              className={`${styles.dynamicItem} ${styles.tocItem}`}
            >
              <input
                type="text"
                value={toc.order}
                onChange={(e) =>
                  handleListObjectChange(
                    "tableOfContents",
                    index,
                    "order",
                    e.target.value
                  )
                }
                placeholder="Order"
                className={styles.tocOrderInput}
              />
              <input
                type="text"
                value={toc.item}
                onChange={(e) =>
                  handleListObjectChange(
                    "tableOfContents",
                    index,
                    "item",
                    e.target.value
                  )
                }
                placeholder="TOC Item"
                className={styles.tocItemInput}
              />
              {bookData.tableOfContents.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem("tableOfContents", index)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              handleAddItem("tableOfContents", { order: "", item: "" })
            }
            className={styles.addButton}
          >
            Add More
          </button>
        </div>

        <button type="submit" className={styles.submitButton}>
          {isEditing ? "Update Book" : "SUBMIT"}
        </button>
      </form>
    </div>
  );
}
